function normalizePath(value = '') {
  const parts = String(value || '')
    .replace(/\\/g, '/')
    .replace(/^[A-Za-z]:\/?/, '')
    .split('/')
    .filter((part) => part && part !== '.')
  const safe = []
  parts.forEach((part) => {
    if (part === '..') safe.pop()
    else safe.push(part)
  })
  return safe.join('/')
}

function pathKey(value = '') {
  return normalizePath(value).toUpperCase()
}

function basename(value = '') {
  return normalizePath(value).split('/').filter(Boolean).pop() || ''
}

function dirname(value = '') {
  const parts = normalizePath(value).split('/').filter(Boolean)
  parts.pop()
  return parts.join('/')
}

function joinPath(base = '', child = '') {
  return normalizePath([base, child].filter(Boolean).join('/'))
}

function splitDelimitedLine(line = '', delimiter = ',') {
  const values = []
  let current = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"'
        index += 1
      } else quoted = !quoted
    } else if (char === delimiter && !quoted) {
      values.push(current.trim())
      current = ''
    } else current += char
  }
  values.push(current.trim())
  return values
}

function delimiterFor(lines = []) {
  const candidates = [',', ';', '\t', '|']
  const sample = lines.slice(0, 5)
  return candidates
    .map((delimiter) => ({ delimiter, score: sample.reduce((total, line) => total + Math.max(0, splitDelimitedLine(line, delimiter).length - 1), 0) }))
    .sort((left, right) => right.score - left.score)[0]
}

function headerKind(value = '') {
  const normalized = String(value || '').trim().toLowerCase()
  if (/^(?:value|valor|codigo|código|code|id|key|clave)$/.test(normalized)) return 'value'
  if (/^(?:text|texto|label|etiqueta|descripcion|descripción|description|name|nombre)$/.test(normalized)) return 'label'
  return ''
}

export function parseCsvCatalog(content = '') {
  const lines = String(content || '').replace(/^\uFEFF/, '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  if (!lines.length) return []
  const detected = delimiterFor(lines)
  const delimiter = detected?.score > 0 ? detected.delimiter : null
  const rows = delimiter ? lines.map((line) => splitDelimitedLine(line, delimiter)) : lines.map((line) => [line])
  const first = rows[0] || []
  const firstKinds = first.map(headerKind)
  const hasHeader = firstKinds.some(Boolean)
  const labelIndex = Math.max(0, firstKinds.indexOf('label'))
  const valueIndex = firstKinds.indexOf('value') >= 0 ? firstKinds.indexOf('value') : Math.min(1, Math.max(0, first.length - 1))
  const dataRows = hasHeader ? rows.slice(1) : rows
  const seen = new Set()

  return dataRows.map((row) => {
    const label = String(row[labelIndex] ?? row[0] ?? '').trim()
    const value = String(row[valueIndex] ?? row[0] ?? '').trim()
    if (!label && !value) return null
    const option = { label: label || value, value: value || label }
    const key = `${option.label}\u0000${option.value}`
    if (seen.has(key)) return null
    seen.add(key)
    return option
  }).filter(Boolean)
}

function resolveCatalogFile(referencePath, sourcePath, catalogFiles = []) {
  const reference = normalizePath(referencePath)
  const sourceDirectory = dirname(sourcePath)
  const exactKeys = new Set([pathKey(reference), pathKey(joinPath(sourceDirectory, reference))])
  const exact = catalogFiles.find((file) => exactKeys.has(pathKey(file.path)))
  if (exact) return exact

  const suffixMatches = catalogFiles.filter((file) => pathKey(file.path).endsWith(`/${pathKey(reference)}`))
  if (suffixMatches.length === 1) return suffixMatches[0]

  const wantedBasename = basename(reference).toUpperCase()
  const basenameMatches = catalogFiles.filter((file) => basename(file.path).toUpperCase() === wantedBasename)
  return basenameMatches.length === 1 ? basenameMatches[0] : null
}

export function hydrateExternalCatalogs(ir = {}, options = {}) {
  const catalogFiles = (options.catalogFiles || []).map((file) => ({ ...file, path: normalizePath(file.path) }))
  if (!catalogFiles.length) return ir
  const sourcePath = normalizePath(options.sourcePath || options.fileName || '')

  Object.values(ir.catalogs || {}).forEach((catalog) => {
    if (catalog?.catalogSource?.type !== 'csv' || !catalog.catalogSource.path) return
    const file = resolveCatalogFile(catalog.catalogSource.path, sourcePath, catalogFiles)
    if (!file) return
    const parsedOptions = parseCsvCatalog(file.content)
    if (!parsedOptions.length) return
    catalog.options = parsedOptions
    catalog.catalogSource = {
      ...catalog.catalogSource,
      status: 'loaded',
      matchedPath: file.path,
      optionCount: parsedOptions.length,
    }
  })

  return ir
}

